import React, { useEffect, useState, useRef } from "react";
import {
  TextField, Button, Grid, Box, FormControlLabel, FormControl, Switch, InputLabel
} from "@mui/material";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../../Context/Contexts";
import CenteredCard from "../../../components/Card/CenteredCardLayout";
import { CurrencyViewModel, CurrencyRateViewModel } from "../../../Types/ExportTypes";
import { AccountAutoComplete } from "../../../components/AutoComplete/AutoCompletes";
import DefaultActions from "../../../components/Card/DefaultActions";
import LocalizedNumberInputs from "../../../components/Input/LocalizedNumberInputs"
import { useTranslation } from "react-i18next";
import { ToolbarWithSaveHandle } from "../../../components/Toolbar/ToolbarWithSave";
const CurrencyEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
  const { currencyCode } = params || {};
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const { authService } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { goBack, navigate } = useNavigation();
  const [currency, setCurrency] = useState<(CurrencyViewModel & { hasChanges?: boolean; originalValues?: Partial<CurrencyViewModel> }) | null>(null);
  const toolbarRef = useRef<ToolbarWithSaveHandle>(null);

  useEffect(() => {
    if (isLoading) showLoading();
    else hideLoading();
  }, [isLoading, showLoading, hideLoading]);

  useEffect(() => {
    if (currencyCode) {
      setIsLoading(true);
      authService
        .get<CurrencyViewModel>(`/settingsApi/GetCurrency?code=${currencyCode}`)
        .then((response) =>
          setCurrency(
            response
              ? { ...response, originalValues: { ...response }, hasChanges: false }
              : null
          )
        )
        .catch((error: any) => {
          showSnackbar(`${t("common.failedToFetchEntries")}: ${error.response?.data ?? error.message}`, "error");
        })
        .finally(() => setIsLoading(false));
    } else {
      const defaultCurrency: CurrencyViewModel & { originalValues: Partial<CurrencyViewModel>; hasChanges: boolean } = {
        currencyId: null,
        currencyCode: "",
        description: "",
        symbol: "",
        currencyAccount: "",
        roundingPOS: null,
        created: null,
        updated: null,
        createdby: null,
        updatedby: null,
        decimalScale: null,
        currentRate: null,
        createNewCurrency: true,
        originalValues: {},
        hasChanges: false,
      };
      defaultCurrency.originalValues = { ...defaultCurrency };
      setCurrency(defaultCurrency);
    }
  }, [currencyCode, authService]);

  const handleSave = async () => {
    if (!currency) return;
    if (!validateCurrency()) return; // Prevent save if validation fails
    var newItem = currency.createNewCurrency ?? false;

    setIsLoading(true);

    try {
      const response = await authService.post<CurrencyViewModel>("/settingsApi/CreateOrUpdateCurrency", currency);
      setCurrency(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null);
      showSnackbar(t("dialog.savedSuccessfully"), "success");

      if (newItem) {
        navigate({ path: "/settings/currency/edit", params: { currencyCode: response.currencyCode } });
      }
    } catch {
      showSnackbar(t("dialog.saveErrorGeneric"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const validateCurrency = () => {
    if (!currency) {
      showSnackbar(t("dialog.dataMissing"), "error");
      return false;
    }


    if (!currency.currencyCode?.trim()) {
      showSnackbar(t("dialog.currencyCodeRequired"), "warning");
      return false;
    }

    if (!currency.description?.trim()) {
      showSnackbar(t("dialog.currencyDescriptionRequired"), "warning");
      return false;
    }

    if (!currency.currentRate?.rate && currency.currentRate?.rate !== 0) {
      showSnackbar(t("dialog.currencyRateRequired"), "warning");
      return false;
    }

    return true;
  };

  const handleChange = (field: keyof CurrencyViewModel | keyof CurrencyRateViewModel, value: any) => {
    setCurrency((prev) => {
      if (!prev) return null;

      let updated: CurrencyViewModel;

      if (field === "rate") {
        updated = {
          ...prev,
          currentRate: value
            ? {
              currencyCode: prev.currencyCode ?? "",
              rate: value,
              validFrom: prev.currentRate?.validFrom ?? null,
            }
            : null,
        };
      } else if (field === "validFrom") {
        updated = {
          ...prev,
          currentRate: prev.currentRate
            ? { ...prev.currentRate, validFrom: value }
            : { currencyCode: prev.currencyCode ?? "", rate: null, validFrom: value },
        };
      } else {
        updated = { ...prev, [field]: value } as CurrencyViewModel;
      }

      return { ...updated, hasChanges: true };
    });
  };

  const handleCancelChanges = () => {
    setCurrency((prev) => {
      if (!prev || !prev.originalValues) return prev;
      const original = prev.originalValues as CurrencyViewModel;
      return { ...original, originalValues: { ...original }, hasChanges: false };
    });
  };

  const showHasChangesError = () => {
    toolbarRef.current?.showHasChangesError();
  };

  const handleDelete = async () => {
    const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
    if (!isConfirmed) return;

    setIsLoading(true);
    try {
      await authService.post(`/settingsApi/DeleteCurrency`, { currency });
      showSnackbar(t("dialog.deletedSuccessfully"), "success");
      navigate({ path: "/settings/currency/list" });
    } catch (error: any) {
      showSnackbar(`${t("dialog.deleteFailed")} : ${error.response?.data ?? error.message}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <CenteredCard
        ref={toolbarRef}
        title={t("common.edit") + " " + t("common.currency")}
        isLoading={isLoading}
        handleSave={handleSave}
        handleCancel={handleCancelChanges}
        hasChanges={currency?.hasChanges ?? false}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", maxWidth: "fit-content", mb: 2 }}>
          <DefaultActions onSave={handleSave} onDelete={handleDelete} />
        </Box>

        {currency && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={t("common.currencyCode")}
                  value={currency.currencyCode || ""}
                  onChange={(e) => handleChange("currencyCode", e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                  error={!currency.currencyCode || currency.currencyCode.trim() === ""}
                  helperText={
                    !currency.currencyCode?.trim() ? t("help.currencyCodeRequired") : ""
                  }
                  disabled={currency.createNewCurrency != true}
                />
              </Grid>



              <Grid item xs={12}>
                <TextField
                  label={t("common.description")}
                  value={currency.description || ""}
                  onChange={(e) => handleChange("description", e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                  error={!currency.description || currency.description.trim() === ""}
                  helperText={!currency.description || currency.description.trim() === "" ? t("help.descriptionRequired") : ""}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("common.symbol")}
                  value={currency.symbol || ""}
                  onChange={(e) => handleChange("symbol", e.target.value)}
                  fullWidth
                  margin="normal"
                />
              </Grid>


              <Grid item xs={6}>
                <FormControl focused className="col " variant="outlined">

                  <LocalizedNumberInputs
                    label={t("common.rate")}
                    value={currency.currentRate?.rate ?? 0}
                    onValueChange={(value) => {
                      handleChange("rate", value ?? null);
                    }}
                    required
                    error={currency.currentRate?.rate == null}
                    helperText={
                      currency.currentRate?.rate == null ? t("help.currencyRateRequired") : ""
                    }
                    allowNegative={false} // Prevents negative numbers
                    decimalScale={4} // Ensures max 4 decimals
                    thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                    decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                  />
                </FormControl >

              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={t("common.validFrom")}
                  type="date"
                  value={currency.currentRate?.validFrom?.split("T")[0] ?? ""}
                  onChange={(e) => handleChange("validFrom", e.target.value)}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>




              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <AccountAutoComplete
                    key={currency.currencyAccount || null}
                    label={t("common.currencyAccount")}
                    valueId={currency.currencyAccount || null}
                    onChange={(selected) => handleChange("currencyAccount", selected?.value || "")}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={t("common.roundingPos")}
                  type="number"
                  value={currency.roundingPOS ?? ""}
                  onChange={(e) => handleChange("roundingPOS", parseFloat(e.target.value) || null)}
                  fullWidth
                  margin="normal"
                />
              </Grid>

              {/*<Grid item xs={6}>*/}
              {/*    <TextField*/}
              {/*        label={t("common.decimalScale")}*/}
              {/*        type="number"*/}
              {/*        value={currency.decimalScale ?? ""}*/}
              {/*        onChange={(e) => handleChange("decimalScale", parseInt(e.target.value) || null)}*/}
              {/*        fullWidth*/}
              {/*        margin="normal"*/}
              {/*    />*/}
              {/*</Grid>*/}
            </Grid>
          </>
        )}

        <Box sx={{ marginTop: 2, textAlign: "center" }}>
          <Button variant="contained" onClick={handleSave} disabled={isLoading} sx={{ marginRight: 2 }}>
            {t("common.save")}
          </Button>
          <Button variant="outlined" onClick={goBack}>
            {t("common.back")}
          </Button>
        </Box>
      </CenteredCard>


    </>
  );
};

export default CurrencyEditPage;
